import { cva, type VariantProps } from "class-variance-authority";

/**
 * Brand 2.0 marketing button scale.
 *
 * Split from the component the same way `@appcaire/ui`'s `button-variants.ts`
 * is, so the component file exports only a component.
 *
 * Every value is a brand2 token. Two of them dodge the legacy adapter sheet on
 * purpose: `text-coral-fg` and `text-ink-fg` exist because
 * `brand2-marketing-adapter.css` rewrites `.text-white` to a slate body colour,
 * which would turn a coral button's label muddy and an ink-band ghost button's
 * label invisible.
 */
export const marketingButtonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2.5 rounded-lg border border-transparent",
    "box-border whitespace-nowrap font-semibold",
    // Colour and shadow only. The design is explicit: no scale, no bounce.
    "transition-[color,background-color,border-color,box-shadow] duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-text focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-coral-text text-coral-fg hover:bg-coral-action-hover",
        secondary:
          "border-hairline-strong bg-white text-ink hover:border-coral",
        /** On the ink band only — the cream ring offset would vanish there. */
        ghost:
          "border-ink-line bg-ink-fill text-ink-fg hover:bg-ink-line focus-visible:ring-offset-ink",
      },
      size: {
        /** Hero and closing CTA. */
        hero: "h-[52px] px-[26px] text-[15.5px] [&_svg]:size-[17px]",
        /** The secondary-CTA band under the hero. */
        band: "h-[46px] px-5 text-[14.5px] [&_svg]:size-4",
        /** Header. */
        nav: "h-10 px-[18px] text-sm [&_svg]:size-4",
      },
    },
    compoundVariants: [
      // The coral glow is a hero/CTA-scale effect. At header scale it reads as
      // a smudge under the button, so the design withholds it there.
      {
        variant: "primary",
        size: "hero",
        class: "shadow-coral-glow",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "hero",
    },
  },
);

export type MarketingButtonVariantProps = VariantProps<
  typeof marketingButtonVariants
>;
